import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FaSearch } from 'react-icons/fa';
import DataTable from '../components/DataTable/DataTable.jsx';
import Pagination from '../components/Pagination/Pagination.jsx';
import usuariosApi from '../services/usuariosApi.js';
import { colors } from '../utils/colors.js';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0;
`;

const SearchBox = styled.div`
  position: relative;
  min-width: 260px;
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.gray[400]};
  font-size: 13px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 34px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
`;

const columns = [
  { key: 'user_name', label: 'Nome' },
  { key: 'nick_name', label: 'Apelido' },
  { key: 'email', label: 'Email' },
  { key: 'region', label: 'Região' },
  { key: 'status', label: 'Status' },
  {
    key: 'created_at',
    label: 'Criado em',
    render: (row) => new Date(row.created_at).toLocaleDateString('pt-BR'),
  },
];

const DEBOUNCE_MS = 400;

export default function Usuarios() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: perPage };
    if (debouncedSearch) params.search = debouncedSearch;
    usuariosApi
      .list(params)
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [page, perPage, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <Header>
        <Title>Usuários</Title>
        <SearchBox>
          <SearchIcon />
          <SearchInput
            placeholder="Buscar por nome, apelido ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchBox>
      </Header>

      <DataTable columns={columns} rows={items} loading={loading} />
      {!loading && (
        <Pagination
          page={page}
          perPage={perPage}
          total={total}
          onPageChange={setPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}
